import { useCallback, useContext, useEffect, useState } from "react";
import { Logger } from "../../../logger/src/Logger";
import { AdapterContext } from "../contexts/AdapterContext";

/**
 * Custom hook that returns callforward data for a given user.
 *
 * Fetch model (intentionally minimal to avoid hammering the call-forwarding API):
 *   - Post-login the value is fetched exactly once when the adapter's
 *     ReplaySubject is first created (see WebexCallSettingsAdapter.getCallForwardSettings).
 *     The ReplaySubject caches that value and replays it to every subscriber,
 *     so multiple consumers (TopBar, CallForward) share a single network call.
 *   - The only ongoing refresh is the explicit 2-minute polling window driven
 *     by the Call Settings dropdown (see CallForward.tsx). When the user opens
 *     Call Settings we poll briefly so a change made in that panel is reflected,
 *     then stop.
 * The failure-safe behavior in WebexCallSettingsAdapter (bounded retry on
 * transient errors + preserving the last known good value) keeps the pill
 * correct at login without any always-on visibility/online refetch triggers,
 * which previously caused excessive post-login API calls inside the MS Teams
 * iframe.
 *
 * @returns {object}
 */
export default function useCallForwardSetting() {
  const [callForwardSettings, setCallForwardSettings] = useState<string>('');
  const [isAttendantConsoleFeatureEnabled, setIsAttendantConsoleFeatureEnabled] = useState<boolean | null>(null);
  const [isCXEssentialsLicensedUser, setIsCXEssentialsLicensedUser] = useState<boolean | null>(null);


  const ctx = useContext(AdapterContext);

  const onCallForwardUpdate = useCallback((data:string) => {
    setCallForwardSettings(data);
  }, []);

  useEffect(() => {
    const subscription = ctx?.callForwardAdapter
      ?.getCallForwardSettings()
      .subscribe({ next: onCallForwardUpdate });
    return () => {
      subscription?.unsubscribe();
    };
  }, [ctx?.callForwardAdapter, onCallForwardUpdate]);

  const refreshCallForward = useCallback(async () => {
    if (ctx?.callForwardAdapter?.refreshCallForward) {
      Logger.debug("[CallForward] refresh requested");
      return ctx?.callForwardAdapter?.refreshCallForward();
    }
    return Promise.resolve();
  }, [ctx?.callForwardAdapter]);

  const onAttendentConsoleUpdate = useCallback((data:boolean) => {
    setIsAttendantConsoleFeatureEnabled(data);
  }, []);

  const onCXEssentialsUpdate = useCallback((data:boolean) => {
    setIsCXEssentialsLicensedUser(data);
  }, []);

  useEffect(() => {
    const featureToggleSubscription = ctx?.callForwardAdapter?.checkAttendantConsoleFeatureToggle()
      ?.subscribe({ next: onAttendentConsoleUpdate });
    return () => {
      if (featureToggleSubscription) {
        featureToggleSubscription.unsubscribe();
      }
    };
  }, [ctx?.callForwardAdapter, onAttendentConsoleUpdate]);
  
  useEffect(() => {
    const cxessentialfeatureSubscription = ctx?.callForwardAdapter?.checkCXEssentialsFeature()
      ?.subscribe({ next: onCXEssentialsUpdate });
    return () => {
      if (cxessentialfeatureSubscription) {
        cxessentialfeatureSubscription.unsubscribe();
      }
    };
  }, [ctx?.callForwardAdapter, onCXEssentialsUpdate]);

  return {
    callForwardSettings,
    refreshCallForward,
    isAttendantConsoleFeatureEnabled,
    isCXEssentialsLicensedUser
  } as const;
}
